import React, { useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { Link as RouterLink } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Layout } from 'features/common/components/Layout';
import { useCurrentUserTeam } from 'features/auth/hooks/useCurrentUserTeam';
import { isAdminOrSuperAdmin, isSuperAdmin } from 'features/auth/helpers';
import { isSalesTeam } from 'utils/slug';
import routes from 'constants/routes';
import { BranchCashDetail } from 'features/branchCash/components/BranchCashDetail';
import { CashChicaPanel } from 'features/branchCash/components/CashChicaPanel';
import { DepositActsList } from 'features/branchCash/components/DepositActsList';
import { DepositWizard } from 'features/branchCash/components/DepositWizard';
import { DepositActDetail } from 'features/branchCash/components/DepositActDetail';
import { CashBoxesOverview } from 'features/branchCash/components/CashBoxesOverview';
import { CashTargetsPanel } from 'features/branchCash/components/CashTargetsPanel';
import { TEAM_CASH_BRANCH, HQ_CASH_BRANCH, VAULT_BRANCHES } from 'features/branchCash/constants';

// Mapa inverso: branch slug → nombre canónico del equipo
const BRANCH_TO_TEAM: Record<string, string> = Object.fromEntries(
	Object.entries(TEAM_CASH_BRANCH).map(([team, branch]) => [branch, team]),
);

function allowedBranchForAdmin(userTeam: string | undefined): string | undefined {
	if (!userTeam) return undefined;
	return TEAM_CASH_BRANCH[userTeam];
}

function LoadingScreen(): JSX.Element {
	return (
		<Layout>
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
				<CircularProgress />
			</Box>
		</Layout>
	);
}

// ─── Guard reutilizable ────────────────────────────────────────────────────────

function useCashGuard(branchSlug: string): { redirect: string | null; loading: boolean } {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { team: userTeam, loading: teamLoading } = useCurrentUserTeam();

	if (teamLoading) return { redirect: null, loading: true };

	if (!isAdminOrSuperAdmin(user)) {
		return { redirect: user?.name ? routes.salesDashboard.seller(user.name) : '/', loading: false };
	}

	if (isSuperAdmin(user)) return { redirect: null, loading: false };

	// Admin (TL): solo su branch
	const myBranch = allowedBranchForAdmin(userTeam);
	if (!myBranch || myBranch !== branchSlug) {
		const dest = myBranch
			? routes.branchCash.detail(myBranch)
			: isSalesTeam(userTeam) && userTeam
			? routes.salesDashboard.team(userTeam)
			: '/';
		return { redirect: dest, loading: false };
	}

	return { redirect: null, loading: false };
}

function branchTitle(branchSlug: string): string {
	if (branchSlug === HQ_CASH_BRANCH) return 'Caja HQ';
	const team = BRANCH_TO_TEAM[branchSlug];
	return team ? `Caja: ${team}` : `Caja: ${branchSlug}`;
}

// ─── /cajas/:branchSlug — con tabs ────────────────────────────────────────────

export function BranchCashRoute(): JSX.Element {
	const { branchSlug } = useParams<{ branchSlug: string }>();
	const history = useHistory();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { redirect, loading } = useCashGuard(branchSlug);
	const [tab, setTab] = useState(0);

	if (loading) return <LoadingScreen />;
	if (redirect) return <Redirect to={redirect} />;

	const title = branchTitle(branchSlug);
	const teamForDashboard = BRANCH_TO_TEAM[branchSlug];
	const hasVault = VAULT_BRANCHES.includes(branchSlug);

	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				{/* Header */}
				<Box mb={2} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
					<Box display="flex" alignItems="center" gap={1.5}>
						{teamForDashboard && (
							<Button
								size="small"
								variant="outlined"
								startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
								onClick={() => history.push(routes.salesDashboard.team(teamForDashboard))}
								sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
							>
								Dashboard del equipo
							</Button>
						)}
						<Typography variant="h5" fontWeight={700} color="primary">
							{title}
						</Typography>
					</Box>
					<Box display="flex" gap={1}>
						{isSuperAdmin(user) && (
							<Button
								size="small"
								variant="outlined"
								onClick={() => history.push(routes.branchCash.overview)}
								sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
							>
								Todas las cajas
							</Button>
						)}
						<Button
							size="small"
							variant="contained"
							startIcon={<AccountBalanceIcon sx={{ fontSize: 15 }} />}
							component={RouterLink}
							to={routes.branchCash.deposit(branchSlug)}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
						>
							Hacer depósito
						</Button>
					</Box>
				</Box>

				{/* Tabs — "Caja fuerte" solo en sucursales que la usan (VAULT_BRANCHES) */}
				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
					<Tab label="Recibos sin depositar" sx={{ textTransform: 'none' }} />
					<Tab label="Caja chica" sx={{ textTransform: 'none' }} />
					{hasVault && <Tab label="Caja fuerte" sx={{ textTransform: 'none' }} />}
					<Tab label="Actas de depósito" sx={{ textTransform: 'none' }} />
				</Tabs>

				{tab === 0 && <BranchCashDetail branch={branchSlug} />}
				{tab === 1 && <CashChicaPanel branch={branchSlug} />}
				{hasVault && tab === 2 && <CashChicaPanel branch={branchSlug} box="FUERTE" />}
				{tab === (hasVault ? 3 : 2) && <DepositActsList branch={branchSlug} />}
			</Container>
		</Layout>
	);
}

// ─── /cajas/:branchSlug/depositar ─────────────────────────────────────────────

export function DepositWizardRoute(): JSX.Element {
	const { branchSlug } = useParams<{ branchSlug: string }>();
	const history = useHistory();
	const { redirect, loading } = useCashGuard(branchSlug);

	if (loading) return <LoadingScreen />;
	if (redirect) return <Redirect to={redirect} />;

	return (
		<Layout>
			<Container maxWidth="md" sx={{ py: 3 }}>
				<Box mb={3} display="flex" alignItems="center" gap={1.5}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
						onClick={() => history.push(routes.branchCash.detail(branchSlug))}
						sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
					>
						Volver a la caja
					</Button>
					<Typography variant="h5" fontWeight={700} color="primary">
						Hacer depósito — {branchTitle(branchSlug)}
					</Typography>
				</Box>
				<DepositWizard branch={branchSlug} />
			</Container>
		</Layout>
	);
}

// ─── /cajas/actas/:depositId ───────────────────────────────────────────────────

export function DepositActDetailRoute(): JSX.Element {
	const { depositId } = useParams<{ depositId: string }>();
	const history = useHistory();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { loading: teamLoading } = useCurrentUserTeam();

	if (teamLoading) return <LoadingScreen />;

	// Sellers sin acceso
	if (!isAdminOrSuperAdmin(user)) {
		return <Redirect to={user?.name ? routes.salesDashboard.seller(user.name) : '/'} />;
	}

	return (
		<Layout>
			<Container maxWidth="lg" sx={{ py: 3 }}>
				<Box mb={3} display="flex" alignItems="center" gap={1.5}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
						onClick={() => history.goBack()}
						sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
					>
						Volver
					</Button>
					<Typography variant="h5" fontWeight={700} color="primary">
						Acta de depósito #{depositId}
					</Typography>
					{isSuperAdmin(user) && (
						<Button
							size="small"
							variant="outlined"
							onClick={() => history.push(routes.branchCash.deposits)}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap', ml: 'auto' }}
						>
							Todas las actas
						</Button>
					)}
				</Box>
				<DepositActDetail depositId={depositId} />
			</Container>
		</Layout>
	);
}

// ─── /cajas/actas ─────────────────────────────────────────────────────────────

export function DepositActsListRoute(): JSX.Element {
	const history = useHistory();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { team: userTeam, loading: teamLoading } = useCurrentUserTeam();

	if (teamLoading) return <LoadingScreen />;

	// Solo superadmin puede ver todas las actas; un TL puede ver las de su caja desde el tab
	if (!isSuperAdmin(user)) {
		const myBranch = allowedBranchForAdmin(userTeam);
		return <Redirect to={myBranch ? routes.branchCash.detail(myBranch) : '/'} />;
	}

	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				<Box mb={3} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
					<Box display="flex" alignItems="center" gap={1.5}>
						<Button
							size="small"
							variant="outlined"
							startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
							onClick={() => history.push(routes.branchCash.overview)}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
						>
							Todas las cajas
						</Button>
						<Typography variant="h5" fontWeight={700} color="primary">
							Actas de depósito
						</Typography>
					</Box>
				</Box>
				<DepositActsList />
			</Container>
		</Layout>
	);
}

// ─── /cajas/objetivos ─────────────────────────────────────────────────────────

export function CashTargetsRoute(): JSX.Element {
	const history = useHistory();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { loading: teamLoading } = useCurrentUserTeam();

	if (teamLoading) return <LoadingScreen />;

	if (!isSuperAdmin(user)) {
		return <Redirect to={routes.branchCash.overview} />;
	}

	return (
		<Layout>
			<Container maxWidth="lg" sx={{ py: 3 }}>
				<Box mb={3} display="flex" alignItems="center" gap={1.5}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
						onClick={() => history.push(routes.branchCash.overview)}
						sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
					>
						Todas las cajas
					</Button>
					<Typography variant="h5" fontWeight={700} color="primary">
						Objetivos de caja chica
					</Typography>
				</Box>
				<CashTargetsPanel />
			</Container>
		</Layout>
	);
}

// ─── /cajas ───────────────────────────────────────────────────────────────────

export function CashOverviewRoute(): JSX.Element {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { team: userTeam, loading: teamLoading } = useCurrentUserTeam();
	const history = useHistory();

	if (teamLoading) return <LoadingScreen />;

	if (!isSuperAdmin(user)) {
		const dest = isSalesTeam(userTeam) && userTeam ? routes.salesDashboard.team(userTeam) : '/';
		return <Redirect to={dest} />;
	}

	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				<Box mb={3} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
					<Box>
						<Typography variant="h5" fontWeight={700} color="primary">
							Cajas de sucursal
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Efectivo sin depositar en cada sucursal. Cuando una caja se acerca al límite, hay que coordinar el
							depósito en el banco.
						</Typography>
					</Box>
					<Box display="flex" gap={1}>
						<Button
							size="small"
							variant="outlined"
							onClick={() => history.push(routes.branchCash.deposits)}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
						>
							Ver actas
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => history.push(routes.branchCash.targets)}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
						>
							Objetivos de caja chica
						</Button>
					</Box>
				</Box>
				<CashBoxesOverview />
			</Container>
		</Layout>
	);
}

/** Redirige /dashboard/equipo/:teamSlug/caja → /cajas/:branchSlug */
export function OldTeamCashRedirect(): JSX.Element {
	const { teamSlug } = useParams<{ teamSlug: string }>();
	const TEAM_SLUG_TO_BRANCH: Record<string, string> = {
		'centro-uy': 'caja-centro',
		'wtc-uy': 'caja-wtc',
		py: 'caja-wtc-py',
		bo: 'caja-shopping-fidalga-bo',
		co: 'caja-bog-co',
	};
	const branch = TEAM_SLUG_TO_BRANCH[teamSlug];
	return <Redirect to={branch ? routes.branchCash.detail(branch) : routes.branchCash.overview} />;
}
